import { ReactNode, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MARKET } from '@/common/constants/markets';
import { TInsurancePerson } from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { InsurancePaidByEmployeeRow } from '@/web/containers/Insurance/InsuranceUpgrade/InsuranceUpgradePlanPage/components/common/InsurancePaidByEmployeeRow/InsurancePaidByEmployeeRow';
import {
  calculatePersonCost,
  calculateTotals,
} from '@/web/containers/Insurance/InsuranceUpgrade/common/utils';
import { useMarket } from '@/web/hooks/useMarket';
import { LabeledValue, Text } from '@coverflex-tech/hypernova';
import { Alert } from '@coverflex-tech/hypernova';
import { TMoney, moneyToString } from '@coverflex-tech/hypernova-utils';
import css from './InsuranceUpgradePlanCostSummaryTable.module.css';

export const InsuranceUpgradePlanCostSummaryTable = ({
  persons = [],
  hidePersonsList,
  hidePaidByCompany,
  children,
  exemptions,
}: TInsuranceUpgradePlanCostSummaryTableProps) => {
  const { t } = useTranslation();
  const { totalEmployeeCost, totalCompanyCost, includedPersons, totalCost } =
    useMemo(
      () => ({
        ...calculateTotals(persons),
        includedPersons: persons.filter((p) => p.included),
      }),
      [persons]
    );
  const { slug } = useMarket();

  return (
    <div className={css.root}>
      <Text bold>
        {t('dashboard.insurance_detail.policy.summary_table.title')}
      </Text>
      {!hidePersonsList && includedPersons.length > 1 && (
        <>
          {includedPersons.map((p) => (
            <LabeledValue
              orientation="horizontal"
              key={p.id}
              label={<Text>{p.shortName}</Text>}
              value={
                <Text>
                  {moneyToString(calculatePersonCost(p), { integer: true })}
                </Text>
              }
            />
          ))}
        </>
      )}
      {(totalCost.amount > 0 || totalCompanyCost.amount > 0) && (
        <>
          <div className={css.divider}></div>
          {totalCost.amount > 0 && (
            <LabeledValue
              orientation="horizontal"
              label={
                <Text>
                  {t('dashboard.insurance.upgrade_plan.card.total_cost_label')}
                </Text>
              }
              value={moneyToString(totalCost)}
            />
          )}
          {totalCompanyCost.amount > 0 && (
            <>
              {slug === MARKET.PT && children && <div>{children}</div>}
              {!hidePaidByCompany && (
                <LabeledValue
                  orientation="horizontal"
                  label={
                    <Text>
                      {t(
                        'dashboard.insurance.upgrade_plan.card.company_total_label'
                      )}
                    </Text>
                  }
                  value={`-${moneyToString(totalCompanyCost)}`}
                />
              )}
            </>
          )}
          <div className={css.divider}></div>
        </>
      )}
      <InsurancePaidByEmployeeRow cost={totalEmployeeCost} />
      {slug === MARKET.ES && exemptions && exemptions?.amount > 0 && (
        <Alert type="info" size="tiny">
          <Trans
            i18nKey={
              'dashboard.insurance.es.confirmation_details_step.exemptiona_amount.info_alert'
            }
            values={{ amount: moneyToString(exemptions) }}
          />
        </Alert>
      )}
    </div>
  );
};

export type TInsuranceUpgradePlanCostSummaryTableProps = {
  persons?: TInsurancePerson[];
  hidePersonsList?: boolean;
  hidePaidByCompany?: boolean;
  children?: ReactNode;
  exemptions?: TMoney;
};
